import React, { useState, useCallback } from 'react';
import { BaseAccountForm } from './BaseAccountForm';
import { AccountFormField } from '../ui/AccountFormField';
import { PasswordStrengthIndicator } from '../ui/PasswordStrengthIndicator';
import { validatePassword, validatePasswordConfirmation } from '../../utils/accountValidation';
import { ValidationResult } from '../../utils/validation';
import { usePasswordReset } from '../../hooks/usePasswordReset';
import { KeyRound } from 'lucide-react';

export interface PasswordResetFormProps {
  onSuccess: () => void;
  onError: (error: string) => void;
  className?: string;
}

interface FormState {
  newPassword: string;
  confirmPassword: string;
}

interface ValidationState {
  newPassword: ValidationResult;
  confirmPassword: ValidationResult;
}

/**
 * Password reset form component
 * Allows users to set a new password after clicking the reset link
 */
export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  onSuccess,
  onError,
  className
}) => {
  const [formState, setFormState] = useState<FormState>({
    newPassword: '',
    confirmPassword: ''
  });

  const [validationState, setValidationState] = useState<ValidationState>({
    newPassword: { isValid: true },
    confirmPassword: { isValid: true }
  });

  const {
    confirmReset,
    isSubmitting,
    error,
    success,
    clearError,
    clearSuccess
  } = usePasswordReset();

  // Update form field value
  const updateField = useCallback((field: keyof FormState, value: string) => {
    setFormState(prev => ({ ...prev, [field]: value }));

    // Clear messages when user starts typing
    if (error) clearError();
    if (success) clearSuccess();
  }, [error, success, clearError, clearSuccess]);

  // Update validation state
  const updateValidation = useCallback((field: keyof ValidationState, result: ValidationResult) => {
    setValidationState(prev => ({ ...prev, [field]: result }));
  }, []);

  // Validate password confirmation
  const validateNewPasswordConfirmation = useCallback((confirmation: string): ValidationResult => {
    return validatePasswordConfirmation(formState.newPassword, confirmation);
  }, [formState.newPassword]);

  // Check if form is valid
  const isFormValid = () => {
    const { newPassword, confirmPassword } = validationState;
    const hasAllFields = formState.newPassword && formState.confirmPassword;
    const allFieldsValid = newPassword?.isValid && confirmPassword?.isValid;

    return hasAllFields && allFieldsValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    try {
      await confirmReset(formState.newPassword, formState.confirmPassword);

      // Reset form on success
      setFormState({
        newPassword: '',
        confirmPassword: ''
      });

      setValidationState({
        newPassword: { isValid: true },
        confirmPassword: { isValid: true }
      });

      onSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      onError(errorMessage);
    }
  };

  return (
    <BaseAccountForm
      title="Reset Password"
      description="Enter your new password below. Make sure it's strong and secure."
      icon={<KeyRound className="w-5 h-5" />}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      submitText="Reset Password"
      submitDisabled={!isFormValid()}
      error={error}
      success={success}
      onClearError={clearError}
      onClearSuccess={clearSuccess}
      className={className}
    >
      <div className="space-y-6">
        {/* New Password Field */}
        <div className="space-y-3">
          <AccountFormField
            id="new-password"
            label="New Password"
            type="password"
            value={formState.newPassword}
            onChange={(value) => updateField('newPassword', value)}
            onValidation={(result) => updateValidation('newPassword', result)}
            validator={validatePassword}
            placeholder="Enter your new password"
            required
            autoComplete="new-password"
            showPasswordToggle
            helperText="Choose a strong password with at least 6 characters"
          />

          {/* Password Strength Indicator */}
          <PasswordStrengthIndicator
            password={formState.newPassword}
            showLabel
            showRequirements
          />
        </div>

        {/* Confirm Password Field */}
        <AccountFormField
          id="confirm-password"
          label="Confirm New Password"
          type="password"
          value={formState.confirmPassword}
          onChange={(value) => updateField('confirmPassword', value)}
          onValidation={(result) => updateValidation('confirmPassword', result)}
          validator={validateNewPasswordConfirmation}
          placeholder="Confirm your new password"
          required
          autoComplete="new-password"
          showPasswordToggle
          helperText="Re-enter your new password to confirm"
        />
      </div>
    </BaseAccountForm>
  );
};
